import React, { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { DEAL_STAGES } from '../../utils/dealConstants'
import DealDetailModal from './DealDetailModal'
import DealLink from '../shared/DealLink.jsx'

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 300

const STAGE_LABELS = DEAL_STAGES.filter(s => s.id !== '').map(s => s.label)
const DEFAULT_HIDDEN_STAGES = ['Closed Traded', 'Closed Lost', 'Artifacts']
const DEFAULT_SELECTED_STAGES = STAGE_LABELS.filter(l => !DEFAULT_HIDDEN_STAGES.includes(l))

function DealSearch() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedStages, setSelectedStages] = useState(() => new Set(DEFAULT_SELECTED_STAGES))
  const [stagesOpen, setStagesOpen] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [sortMode, setSortMode] = useState('created_desc')
  const [expanded, setExpanded] = useState(new Set())
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [openingDealId, setOpeningDealId] = useState(null)

  const debounceTimer = useRef(null)
  const requestSeq = useRef(0)
  const stagesButtonRef = useRef(null)
  const stagesPanelRef = useRef(null)

  useEffect(() => {
    if (!stagesOpen) return
    function handleOutside(e) {
      const inButton = stagesButtonRef.current && stagesButtonRef.current.contains(e.target)
      const inPanel = stagesPanelRef.current && stagesPanelRef.current.contains(e.target)
      if (!inButton && !inPanel) setStagesOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [stagesOpen])

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([])
      setTruncated(false)
      setHasSearched(false)
      setError(null)
      return
    }

    debounceTimer.current = setTimeout(() => {
      runSearch(trimmed)
    }, DEBOUNCE_MS)

    return () => clearTimeout(debounceTimer.current)
  }, [query])

  async function runSearch(q) {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(null)
    try {
      const response = await api.get(`/api/deals/search?q=${encodeURIComponent(q)}`)
      if (seq !== requestSeq.current) return
      setResults(response.results || [])
      setTruncated(!!response.truncated)
      setHasSearched(true)
    } catch (e) {
      if (seq !== requestSeq.current) return
      console.error('Deal search failed:', e)
      setError(e?.message || 'Search failed')
      setResults([])
      setTruncated(false)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }

  const visible = useMemo(() => {
    return results.filter(r => selectedStages.has(r.deal_stage_label))
  }, [results, selectedStages])

  const groupedDeals = useMemo(() => {
    const map = new Map()
    for (const row of visible) {
      const id = row.hubspot_deal_id
      if (!map.has(id)) {
        map.set(id, {
          hubspot_deal_id: id,
          deal_name: row.deal_name,
          deal_stage_label: row.deal_stage_label,
          deal_stage: row.deal_stage,
          deal_create_date: row.deal_create_date,
          received_at: row.received_at,
          awaiting_payment_at: row.awaiting_payment_at,
          closed_traded_at: row.closed_traded_at,
          matchedOnDealName: false,
          items: []
        })
      }
      const group = map.get(id)
      if (row.matched_on === 'deal_name') group.matchedOnDealName = true
      if (row.item_name) {
        group.items.push({
          item_name: row.item_name,
          product_category: row.product_category,
          condition: row.condition,
          buy_price_cents: row.buy_price_cents,
          sell_price_cents: row.sell_price_cents,
          item_index: row.item_index,
          matched_on: row.matched_on
        })
      }
    }
    return Array.from(map.values())
  }, [visible])

  const sortedDeals = useMemo(() => {
    const arr = [...groupedDeals]
    const firstSP = (g) => g.items.find(i => i.sell_price_cents != null)?.sell_price_cents ?? null
    const firstBP = (g) => g.items.find(i => i.buy_price_cents != null)?.buy_price_cents ?? null
    const cmpStr = (a, b) => String(a ?? '').localeCompare(String(b ?? ''))
    const cmpNumDesc = (a, b) => {
      if (a == null && b == null) return 0
      if (a == null) return 1
      if (b == null) return -1
      return b - a
    }
    const cmpNumAsc = (a, b) => -cmpNumDesc(a, b)
    switch (sortMode) {
      case 'created_desc': arr.sort((a, b) => cmpStr(b.deal_create_date, a.deal_create_date)); break
      case 'created_asc': arr.sort((a, b) => cmpStr(a.deal_create_date, b.deal_create_date)); break
      case 'stage': arr.sort((a, b) => cmpStr(a.deal_stage_label, b.deal_stage_label)); break
      case 'deal_name': arr.sort((a, b) => cmpStr(a.deal_name, b.deal_name)); break
      case 'deal_id': arr.sort((a, b) => cmpStr(a.hubspot_deal_id, b.hubspot_deal_id)); break
      case 'sp_desc': arr.sort((a, b) => cmpNumDesc(firstSP(a), firstSP(b))); break
      case 'sp_asc': arr.sort((a, b) => cmpNumAsc(firstSP(a), firstSP(b))); break
      case 'bp_desc': arr.sort((a, b) => cmpNumDesc(firstBP(a), firstBP(b))); break
      case 'bp_asc': arr.sort((a, b) => cmpNumAsc(firstBP(a), firstBP(b))); break
    }
    return arr
  }, [groupedDeals, sortMode])

  const dealCount = groupedDeals.length
  const itemCount = visible.length
  const totalValuePence = groupedDeals.reduce((sum, g) => {
    // sell_price_cents is per unit — the stat is qty-aware like Prism's totals.
    return sum + g.items.reduce((s, it) => s + (it.sell_price_cents || 0) * (it.quantity || 1), 0)
  }, 0)
  const stageCounts = groupedDeals.reduce((acc, g) => {
    const s = g.deal_stage_label || g.deal_stage || 'Unknown'
    acc[s] = (acc[s] || 0) + 1
    return acc
  }, {})
  const topStages = Object.entries(stageCounts).sort((a, b) => b[1] - a[1]).slice(0, 3)

  const hiddenCount = results.length - visible.length

  const stagesSummaryLabel = (() => {
    const total = STAGE_LABELS.length
    const n = selectedStages.size
    if (n === total) return 'All'
    if (n === 0) return 'None'
    if (n === 1) return [...selectedStages][0]
    return `${n} of ${total}`
  })()

  async function openDealById(dealId) {
    if (openingDealId) return
    setOpeningDealId(dealId)
    try {
      const res = await api.get(`/api/deals/${dealId}`)
      if (res.deal) setSelectedDeal(res.deal)
    } catch (e) {
      console.error('Failed to open deal:', e)
    } finally {
      setOpeningDealId(null)
    }
  }

  function toggleExpanded(id) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function badgeClass(label) {
    if (!label) return 'badge'
    if (label === 'Closed Traded') return 'badge badge-success'
    if (label === 'Closed Lost') return 'badge badge-danger'
    if (label === 'Awaiting Payment') return 'badge badge-warning'
    if (label === 'Issue with Item' || label === 'Problem With Shipping') return 'badge badge-danger'
    return 'badge'
  }

  function renderItemPrices(item) {
    return (
      <div style={{ display: 'flex', gap: 'var(--space-3)', fontSize: '0.8rem', marginTop: 4 }}>
        {item.sell_price_cents != null && (
          <span style={{ color: 'var(--color-success)' }}>SP {formatCurrency(item.sell_price_cents / 100)}</span>
        )}
        {item.buy_price_cents != null && (
          <span style={{ color: 'var(--color-info)' }}>BP {formatCurrency(item.buy_price_cents / 100)}</span>
        )}
      </div>
    )
  }

  function itemDetailsLine(item) {
    const parts = []
    if (item.product_category) parts.push(item.product_category)
    if (item.condition) parts.push(item.condition)
    return parts.join(' · ')
  }

  function exportCSV() {
    const escCSV = (val) => {
      const s = String(val ?? '')
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? '"' + s.replace(/"/g, '""') + '"'
        : s
    }
    const fmtPrice = (cents) => (cents == null ? '' : (cents / 100).toFixed(2))
    const fmtDate = (d) => {
      if (!d) return ''
      const dt = new Date(d)
      return isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 10)
    }

    const rows = [['Deal ID', 'Deal Name', 'Stage', 'Created', 'Item #', 'Item Name', 'Category', 'Condition', 'SP', 'BP']]

    for (const group of sortedDeals) {
      const stage = group.deal_stage_label || group.deal_stage || ''
      const created = fmtDate(group.deal_create_date)

      if (group.items.length === 0) {
        rows.push([group.hubspot_deal_id, group.deal_name || '', stage, created, '', '(no matched items)', '', '', '', ''])
      } else {
        group.items.forEach((item, idx) => {
          rows.push([
            group.hubspot_deal_id,
            group.deal_name || '',
            stage,
            created,
            item.item_index != null ? item.item_index : idx + 1,
            item.item_name || '',
            item.product_category || '',
            item.condition || '',
            fmtPrice(item.sell_price_cents),
            fmtPrice(item.buy_price_cents)
          ])
        })
      }
    }

    const csv = rows.map(r => r.map(escCSV).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `deals-search-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      {/* Stats grid (mirrors AllDeals) */}
      {hasSearched && dealCount > 0 && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{dealCount}</div>
            <div className="stat-label">Deals Found</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{itemCount}</div>
            <div className="stat-label">Matched Items</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrency(totalValuePence / 100)}</div>
            <div className="stat-label">Total SP</div>
          </div>
          {topStages.map(([stage, count], i) => (
            <div className="stat-card" key={i}>
              <div className="stat-value">{count}</div>
              <div className="stat-label">{stage}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search card */}
      <div className="card" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        <div className="filter-section">
          <div className="filter-group filter-group-wide" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label className="filter-label">Search (deal name or item)</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. fender strat, gibson les paul, marshall amp…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && query.trim().length >= MIN_QUERY_LENGTH) {
                  if (debounceTimer.current) clearTimeout(debounceTimer.current)
                  runSearch(query.trim())
                }
              }}
              autoFocus
            />
            <button
              type="button"
              className="btn btn-primary"
              disabled={query.trim().length < MIN_QUERY_LENGTH || loading}
              onClick={() => {
                if (debounceTimer.current) clearTimeout(debounceTimer.current)
                runSearch(query.trim())
              }}
              style={{ alignSelf: 'flex-start' }}
            >
              Submit
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="card" style={{ padding: 'var(--space-4)' }}>Searching…</div>}

      {error && (
        <div className="card" style={{ padding: 'var(--space-4)', color: 'var(--color-danger)' }}>
          {error}
        </div>
      )}

      {truncated && !loading && !error && (
        <div className="card" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-3)', background: 'var(--color-warning-bg, #fff8e6)' }}>
          <strong>Showing first 500 matches.</strong> Refine your search to see specific items.
        </div>
      )}

      {hasSearched && !loading && !error && results.length === 0 && (
        <div className="card empty-state">
          <div className="empty-state-title">No Deals Found</div>
          <div className="empty-state-description">Try a different search term.</div>
        </div>
      )}

      {hasSearched && !loading && !error && results.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          {/* Header bar: count + filters + sort */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--space-3)',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
            borderTopLeftRadius: 'var(--radius-md, 6px)',
            borderTopRightRadius: 'var(--radius-md, 6px)',
          }}>
            <span className="text-muted">
              <strong>{dealCount}</strong> deals · <strong>{itemCount}</strong> items
              {hiddenCount > 0 && <> · {hiddenCount} hidden by toggles</>}
            </span>
            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={exportCSV}
                disabled={dealCount === 0}
                style={{ fontSize: '0.85rem', padding: '6px 12px' }}
              >
                Export CSV
              </button>
              <div style={{ position: 'relative' }}>
                <button
                  ref={stagesButtonRef}
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setStagesOpen(o => !o)}
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: '0.85rem', padding: '6px 12px' }}
                >
                  Stages: {stagesSummaryLabel}
                  <span style={{ fontSize: '0.7rem' }}>{stagesOpen ? '▲' : '▼'}</span>
                </button>
                {stagesOpen && stagesButtonRef.current && createPortal(
                  (() => {
                    const rect = stagesButtonRef.current.getBoundingClientRect()
                    const panelWidth = 280
                    const top = rect.bottom + 4
                    const left = Math.min(rect.right - panelWidth, window.innerWidth - panelWidth - 8)
                    return (
                      <div
                        ref={stagesPanelRef}
                        style={{
                          position: 'fixed',
                          top,
                          left: Math.max(8, left),
                          zIndex: 9999,
                          background: 'var(--color-bg, #fff)',
                          border: '1px solid var(--color-border, #e2e8f0)',
                          borderRadius: 'var(--radius-md, 6px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          width: panelWidth,
                          maxHeight: 360,
                          overflowY: 'auto',
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-3)',
                          borderBottom: '1px solid var(--color-border)',
                          background: 'var(--color-bg-secondary)',
                          position: 'sticky',
                          top: 0,
                        }}>
                          <button type="button" className="btn btn-xs btn-secondary" onClick={() => setSelectedStages(new Set(STAGE_LABELS))}>All</button>
                          <button type="button" className="btn btn-xs btn-secondary" onClick={() => setSelectedStages(new Set())}>Clear</button>
                          <button type="button" className="btn btn-xs btn-secondary" onClick={() => setSelectedStages(new Set(DEFAULT_SELECTED_STAGES))}>Reset</button>
                        </div>
                        {STAGE_LABELS.map(label => (
                          <label
                            key={label}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-2)',
                              padding: 'var(--space-2) var(--space-3)',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              borderBottom: '1px solid var(--color-border)',
                            }}
                            className="hover-row"
                          >
                            <input
                              type="checkbox"
                              checked={selectedStages.has(label)}
                              onChange={e => {
                                setSelectedStages(prev => {
                                  const next = new Set(prev)
                                  if (e.target.checked) next.add(label)
                                  else next.delete(label)
                                  return next
                                })
                              }}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    )
                  })(),
                  document.body
                )}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: '0.85rem' }}>
                Sort:
                <select className="form-input" style={{ padding: '4px 8px', fontSize: '0.85rem' }} value={sortMode} onChange={e => setSortMode(e.target.value)}>
                  <option value="created_desc">Created (newest)</option>
                  <option value="created_asc">Created (oldest)</option>
                  <option value="stage">Stage</option>
                  <option value="deal_name">Deal name (A–Z)</option>
                  <option value="deal_id">Deal ID</option>
                  <option value="sp_desc">Sell price (high)</option>
                  <option value="sp_asc">Sell price (low)</option>
                  <option value="bp_desc">Buy price (high)</option>
                  <option value="bp_asc">Buy price (low)</option>
                </select>
              </label>
            </div>
          </div>

          {/* Card list */}
          {dealCount === 0 ? (
            <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>All Results Hidden</div>
              <div style={{ fontSize: '0.85rem' }}>
                {hiddenCount} match{hiddenCount === 1 ? '' : 'es'} hidden by your filters. Adjust the Stages dropdown above to see results.
              </div>
            </div>
          ) : (
          <div style={{ padding: 'var(--space-3)' }}>
            {sortedDeals.map(group => {
              const firstItem = group.items[0]
              const moreCount = group.items.length > 1 ? group.items.length - 1 : 0
              const isExpanded = expanded.has(group.hubspot_deal_id)
              const isOpening = openingDealId === group.hubspot_deal_id

              const primaryName = firstItem?.item_name || group.deal_name || `Deal ${group.hubspot_deal_id}`

              return (
                <div key={group.hubspot_deal_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <div
                    onClick={() => openDealById(group.hubspot_deal_id)}
                    style={{
                      padding: 'var(--space-3)',
                      cursor: isOpening ? 'wait' : 'pointer',
                      display: 'flex',
                      gap: 'var(--space-3)',
                      alignItems: 'flex-start',
                      opacity: isOpening ? 0.6 : 1,
                    }}
                    className="hover-row"
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          background: firstItem?.matched_on === 'item_name' ? 'var(--color-highlight, #fffbe6)' : 'transparent',
                          padding: firstItem?.matched_on === 'item_name' ? '0 4px' : 0,
                          borderRadius: 3,
                        }}>
                          {primaryName}
                        </span>
                        {moreCount > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(group.hubspot_deal_id) }}
                            style={{
                              fontSize: '0.75rem',
                              color: 'var(--color-primary)',
                              background: 'var(--color-bg-secondary)',
                              padding: '2px 8px',
                              borderRadius: 4,
                              border: 'none',
                              cursor: 'pointer',
                            }}
                          >
                            {isExpanded ? '− hide' : `+${moreCount} more`}
                          </button>
                        )}
                        {group.matchedOnDealName && (
                          <span style={{
                            fontSize: '0.7rem',
                            background: 'var(--color-highlight, #fffbe6)',
                            color: 'var(--color-text-muted)',
                            padding: '2px 6px',
                            borderRadius: 3,
                            border: '1px solid var(--color-border)',
                          }}>
                            matched in deal name
                          </span>
                        )}
                      </div>

                      {firstItem && itemDetailsLine(firstItem) && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                          {itemDetailsLine(firstItem)}
                        </div>
                      )}

                      {firstItem && (firstItem.sell_price_cents != null || firstItem.buy_price_cents != null) && renderItemPrices(firstItem)}

                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', opacity: 0.7 }}><DealLink dealId={group.hubspot_deal_id} onOpen={() => openDealById(group.hubspot_deal_id)} /></span>
                        {group.deal_name && primaryName !== group.deal_name && (
                          <span style={{
                            background: group.matchedOnDealName ? 'var(--color-highlight, #fffbe6)' : 'transparent',
                            padding: group.matchedOnDealName ? '0 4px' : 0,
                            borderRadius: 3,
                          }}>
                            {group.deal_name}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ marginBottom: 4 }}>
                        <span className={badgeClass(group.deal_stage_label)}>
                          {group.deal_stage_label || group.deal_stage || '-'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        {formatDate(group.deal_create_date)}
                      </div>
                    </div>
                  </div>

                  {isExpanded && moreCount > 0 && (
                    <div style={{
                      padding: '0 var(--space-3) var(--space-3) var(--space-3)',
                      background: 'var(--color-bg-secondary)',
                      borderTop: '1px dashed var(--color-border)',
                    }}>
                      {group.items.slice(1).map((item, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: 'var(--space-2) 0',
                            borderBottom: idx < group.items.length - 2 ? '1px solid var(--color-border)' : 'none',
                          }}
                        >
                          <div style={{
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            background: item.matched_on === 'item_name' ? 'var(--color-highlight, #fffbe6)' : 'transparent',
                            padding: item.matched_on === 'item_name' ? '0 4px' : 0,
                            borderRadius: 3,
                            display: 'inline-block',
                          }}>
                            {item.item_name}
                          </div>
                          {itemDetailsLine(item) && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                              {itemDetailsLine(item)}
                            </div>
                          )}
                          {(item.sell_price_cents != null || item.buy_price_cents != null) && renderItemPrices(item)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          )}
        </div>
      )}

      {selectedDeal && (
        <DealDetailModal
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onDealUpdated={() => setSelectedDeal(null)}
        />
      )}
    </div>
  )
}

export default DealSearch
